import { CheckIcon, ClipboardIcon } from '@heroicons/react/20/solid';
import { useEffect, useId, useRef, useState } from 'react';

import { copyToClipboard } from '@/helpers/clipboard';
import { groupDigits as groupDigitsForDisplay } from '@/helpers/files';
import { announce } from '@/lib/announcer';
import { cn } from '@/utils/cn';

import { IconButton } from '../ui/IconButton';

export interface CopyFieldProps {
  /** Already localized. */
  label: string;
  /** The raw value that is copied. Never the grouped display string. */
  value: string;
  displayValue?: string | undefined;
  groupDigits?: boolean;
  monospace?: boolean;
  /** Already localized. Used as the icon-button accessible name. */
  copyLabel: string;
  /** Already localized. Announced and shown after a successful copy. */
  copiedLabel: string;
  /** Already localized. Shown when the Clipboard API is unavailable. */
  manualCopyLabel: string;
  className?: string;
}

const CONFIRM_MS = 1_500;

/**
 * Copies the raw value, never the grouped display string. Pasting spaces into a banking app is how
 * a payment goes to the wrong account.
 *
 * Confirmation is visual *and* announced. A checkmark that is not in the live region is invisible
 * to a screen reader, which is a WCAG failure on the control a blind player needs most.
 */
export function CopyField({
  label,
  value,
  displayValue,
  groupDigits = false,
  monospace = true,
  copyLabel,
  copiedLabel,
  manualCopyLabel,
  className,
}: CopyFieldProps) {
  const valueId = useId();
  const valueRef = useRef<HTMLSpanElement>(null);
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => {
      setCopied(false);
    }, CONFIRM_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  const shown = displayValue ?? (groupDigits ? groupDigitsForDisplay(value) : value);

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setManual(false);
      setCopied(true);
      announce(copiedLabel, 'polite');
      return;
    }

    setManual(true);
    const node = valueRef.current;
    if (node !== null) {
      const selection = window.getSelection();
      if (selection !== null) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    announce(manualCopyLabel, 'polite');
  };

  return (
    <div
      className={cn(
        'bg-surface-muted flex min-h-11 min-w-0 items-center gap-2 rounded-md px-3 py-2',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-text-muted text-xs font-medium">{label}</span>
        <span
          id={valueId}
          ref={valueRef}
          className={cn(
            'text-text min-w-0 break-all',
            monospace && 'font-mono',
            'tabular-nums',
          )}
        >
          {shown}
        </span>
        {manual && <span className="text-text-muted text-xs">{manualCopyLabel}</span>}
      </div>
      <IconButton
        aria-label={copyLabel}
        aria-describedby={valueId}
        className="text-secondary-fg"
        icon={copied ? <CheckIcon /> : <ClipboardIcon />}
        onClick={() => {
          void handleCopy();
        }}
      />
    </div>
  );
}
